import { useState } from 'react'
import { toast } from 'sonner'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkBreaks from 'remark-breaks'
import { Pencil } from 'lucide-react'
import { useTrackerTicket } from '@/queries/tracker'
import { useAdminPatch, extractErrorMessage } from '@/queries/ticketMutations'
import { MessageThread } from '@/features/ticket-detail/MessageThread'
import { ReplyComposer } from '@/features/ticket-detail/ReplyComposer'
import { AttachmentList } from '@/features/attachments/AttachmentList'
import { FullPageSpinner } from '@/components/etainabl-spinner'
import { ViewingAsBanner } from '@/features/view-as/ViewingAsBanner'
import { Input } from '@/components/ui/input'
import { isAdminViewer } from '@/lib/scope'
import { TrackerPropertyBand } from './TrackerPropertyBand'
import { TrackerImpactSection } from './TrackerImpactSection'
import { CopyForClaude } from './CopyForClaude'
import { formatTicketRef } from '@/lib/ticketRef'
import { formatDateTime } from '@/lib/dates'
import type { TicketMessage } from '@/types/ticket'

// The opening message is the reporter's own words — possibly already
// emailed — so it stays plain read-only markup here. Edits belong in
// Impact or a reply, never a rewrite of what was said.
function DescriptionCard({ ticketId, message }: { ticketId: string; message: TicketMessage }) {
  return (
    <section className="mb-[22px]" data-testid="tracker-description">
      <h2 className="mb-2 text-etn-micro font-semibold uppercase tracking-[0.05em] text-etn-text-label">Description</h2>
      <div className="rounded-lg border border-border bg-etn-surface px-[18px] py-4 shadow-etn-whisper">
        <div className="mb-2 flex flex-wrap items-center gap-2 text-etn-micro text-etn-text-soft">
          <span className="font-semibold text-etn-control text-etn-text-cell">{message.author?.name ?? 'Unknown'}</span>
          <span>{formatDateTime(message.createdAt)}</span>
        </div>
        <div className="prose prose-sm dark:prose-invert max-w-none text-etn-text-cell">
          <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>{message.body}</ReactMarkdown>
        </div>
        <AttachmentList ticketId={ticketId} attachments={message.attachments} />
      </div>
    </section>
  )
}

// Staff click-to-edit affordance for the subject — same pattern and mutation
// (useAdminPatch's { subject }) as PresentationView's PresentationCard.
// Kept local: this header's layout (ref + title + Copy button on one row)
// shares nothing else with the deck card.
function EditableTitle({ ticketId, subject }: { ticketId: string; subject: string }) {
  const [editing, setEditing] = useState(false)
  const patch = useAdminPatch(ticketId)

  const commit = async (value: string) => {
    setEditing(false)
    const next = value.trim()
    if (!next || next === subject) return
    try {
      await patch.mutateAsync({ subject: next })
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Could not update subject'))
    }
  }

  if (editing) {
    return (
      <Input
        aria-label="Subject"
        autoFocus
        defaultValue={subject}
        disabled={patch.isPending}
        onKeyDown={e => {
          if (e.key === 'Escape') {
            e.preventDefault()
            setEditing(false)
          } else if (e.key === 'Enter') {
            e.preventDefault()
            e.currentTarget.blur()
          }
        }}
        onBlur={e => commit(e.target.value)}
        className="h-auto flex-1 py-1 text-2xl font-semibold"
      />
    )
  }

  return (
    <button
      type="button"
      aria-label="Edit subject"
      onClick={() => setEditing(true)}
      className="group flex min-w-0 flex-1 items-center gap-2 text-left"
    >
      <h1 className="min-w-0 truncate text-2xl font-semibold text-foreground">{subject}</h1>
      <Pencil className="h-4 w-4 shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100" aria-hidden />
    </button>
  )
}

export function TrackerDetailView({ ticketId, asUser }: { ticketId: string; asUser?: string }) {
  const { data, isLoading, error } = useTrackerTicket(ticketId, asUser)
  if (isLoading) return <FullPageSpinner />

  // A forged asUser on a non-admin session gets an explicit message, not a
  // blank "not found" that reads as a broken link — mirrors TicketDetailView.
  const status = (error as { status?: number; statusCode?: number } | null)?.status
    ?? (error as { statusCode?: number } | null)?.statusCode
  if (status === 403) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        You don&apos;t have access to view as another user.
      </div>
    )
  }

  if (error || !data) return <div className="p-8">Ticket not found.</div>

  const isStaff = isAdminViewer(data.viewerRelation)
  // The opening reply is the ticket's own description (see CreateTicketForm)
  // — rendered once as the Description card, never again in the comments
  // list below. `find`, not `messages[0]`: skips a leading system row if one
  // ever precedes it, rather than baking that ordering assumption in.
  const opening = data.messages.find(m => m.kind === 'reply')
  const comments = opening ? data.messages.filter(m => m._id !== opening._id) : data.messages

  return (
    <div>
      {data.viewingAs && <ViewingAsBanner name={data.viewingAs.name} companyName={data.viewingAs.companyName} />}

      <div className="mb-1 flex items-start gap-3.5">
        <span className="shrink-0 whitespace-nowrap pt-2 text-etn-control font-semibold text-etn-text-soft tabular-nums">
          {formatTicketRef(data.ticket)}
        </span>
        {isStaff ? (
          <EditableTitle ticketId={ticketId} subject={data.ticket.subject} />
        ) : (
          <h1 className="min-w-0 flex-1 truncate text-2xl font-semibold text-foreground">{data.ticket.subject}</h1>
        )}
        {isStaff && <CopyForClaude ticketId={ticketId} />}
      </div>

      <TrackerPropertyBand ticket={data.ticket} viewerRelation={data.viewerRelation} />

      {opening && <DescriptionCard ticketId={ticketId} message={opening} />}

      <TrackerImpactSection ticket={data.ticket} viewerRelation={data.viewerRelation} />

      <section>
        <h2 className="mb-2 text-etn-micro font-semibold uppercase tracking-[0.05em] text-etn-text-label">Comments</h2>
        <div data-testid="tracker-comments">
          <MessageThread ticketId={ticketId} messages={comments} />
        </div>
        <div className="mt-3">
          {data.viewingAs ? (
            <div className="rounded-lg border border-border bg-muted px-4 py-3 text-sm text-muted-foreground">
              This is a read-only view — replies are disabled while viewing as another user.
            </div>
          ) : (
            <ReplyComposer ticketId={ticketId} status={data.ticket.status} viewer={data.viewerRelation} />
          )}
        </div>
      </section>
    </div>
  )
}
